import { Injectable } from '@angular/core';
import { ProjectService } from '../project/project.service';
import { Subject, BehaviorSubject, Observable } from 'rxjs';
import { first, filter } from 'rxjs/operators';
import { ApiService } from '../../api/api.service';
import { BasicArea } from './basic-area.interface';
import { AreaService } from '../area/area.service';
import { ApiLegacyService } from '../../api/legacy/api-legacy.service';

/**
 * Basic area core service.
 *
 * Contains all the private logic for Basic area management.
 *
 * **Only BasicAreaService is allowed to access this Service.**
 */
@Injectable({
  providedIn: 'root'
})
export class BasicAreaCoreService {

  /** Module subject. */
  protected _basicAreas: Subject<BasicArea[]> = new BehaviorSubject<BasicArea[]>(null);

  /** Module observable. */
  public basicArea$ = this._basicAreas.asObservable();

  /** Constructor. */
  constructor(
    protected apiService: ApiService,
    protected apiLegacyService: ApiLegacyService,
    protected areaService: AreaService,
    protected projectService: ProjectService,
  ) {
    this._trackProject();
  }


  /**
   * Get basic areas.
   *
   * @returns The current project's basic areas.
   */
  public get(): BasicArea[] {
    let basicAreas: BasicArea[];
    this.basicArea$.pipe(first()).subscribe(m => basicAreas = m);
    return basicAreas;
  }

  /**
   * Get specific basic area.
   *
   * @param id The id of the basic area.
   * @returns The requested basic area.
   */
  public getBasicArea(id: BasicArea['_id']): BasicArea {
    return this.get().find(ba => ba._id === id);
  }

  /**
   * Delete area template.
   *
   * @param areaTemplate The area template to delete.
   * @returns Empty observable on success.
   */
  protected _delete(areaTemplate: BasicArea): Observable<{}> {
    return new Observable(observer => {
      this.apiService.deleteAreaTemplate(areaTemplate).subscribe(response => {
        this.projectService.patchBasicArea(areaTemplate, { active: false });
        observer.next(true);
        observer.complete();
      });
    });
  }

  /**
   * When updating the basic area's name, make sure the name is unique.
   *
   * Create a list of all matching names, ignoring the suffix (N).
   * If the list contains matches, increment the suffix, otherwise just return the name.
   *
   * @param name The new name to check for duplicates.
   * @returns The (updated) name.
   */
  protected _checkNameUnique(name: BasicArea['name']): BasicArea['name'] {
    const matches = this.get().filter(ba => ba.name.replace(/\([0-9]*\)/, '').trim() === name.trim());
    return matches.length
      ? `${ name.replace(/\([0-9]*\)/, '').trim() } (${ matches.length + 1 })`
      : name;
  }

  /**
   * Fix basic areas.
   *
   * Patches:
   *  - projectLevel: Set on init by previous datastore (utils/stores/project.initializer.service), but is deprecated.
   *     When deleted, we should not lose this.
   *
   * @param basicAreas The basic areas to fix.
   * @returns Fixed basic areas.
   */
  private _fix(basicAreas: BasicArea[]): BasicArea[] {
    for (const basicArea of basicAreas) {
      basicArea.projectLevel = 4;
    }
    return basicAreas;
  }

  /**
   * Patch basic area.
   *
   * @param basicArea The basic area to patch.
   * @param values The updated values.
   * @param [config] Optional configuration.
   * @returns Observable with state.
   */
  protected _patchBasicArea(basicArea: BasicArea, values: Partial<BasicArea>, config?: any): Observable<boolean> {
    return new Observable(observer => {
      this.apiService.updateAreaTemplate(basicArea, values).subscribe(response => {
        this.projectService.patchBasicArea(basicArea, response);
        observer.next(true);
        observer.complete();
      });
    });
  }

  /**
   * Track project and set basic areas.
   */
  protected _trackProject(): void {
    this.projectService.project$.pipe(
      filter(project => project !== null)
    ).subscribe(project => {
      this._basicAreas.next(this._fix(project.basicAreas));
    });
  }
}
